import type { EntityAction, FireCMSContext } from "@firecms/core";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import type { ReactElement } from "react";
import {
  DeleteIcon,
  LinkIcon,
  SettingsIcon,
  SyncAltIcon,
  UnarchiveIcon,
  ArchiveIcon,
} from "@firecms/ui";
import type { Event } from "../collections/events";
import type { EventStatus } from "../properties/eventStatus";
import {
  EVENT_ARCHIVABLE_STATUSES,
  EVENT_OPERATIONAL_STATUSES,
  isEventManagerAccessible,
} from "../properties/eventStatus";
import {
  buildEventAdminUrl,
  copyTextToClipboard,
} from "../workspace/adminLinks";
import { EVENT_MANAGER_LABEL } from "../workspace/workspaceLabels";
import { openConfirmTextAction } from "./openConfirmTextAction";
import { openDeleteEventAction } from "./openDeleteEventAction";
import { tableOnlyEntityAction } from "./entityActionDefaults";
import { openEventFromEntity } from "./workspaceNavigationBridge";
import { managedEventKey } from "../firebase/membershipQueries";
import { getAdminT } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import type { Locale } from "../localization/constants";

export type EventEntityActionsOptions = {
  accountId: string;
  isSuperManager?: boolean;
  isAccountManagerForAccount?: boolean;
  managedEventKeys?: Set<string>;
  archivedView?: boolean;
};

const isAccountLifecycleManager = (
  options: EventEntityActionsOptions
): boolean =>
  Boolean(options.isSuperManager || options.isAccountManagerForAccount);

const callSetEventStatus = async (
  context: FireCMSContext,
  accountId: string,
  eventId: string,
  status: EventStatus,
  locale: Locale
): Promise<boolean> => {
  const t = getAdminT(locale);
  try {
    const functions = getFunctions(getApp());
    const callable = httpsCallable(functions, "setEventStatus");
    await callable({ accountId, eventId, status });
    context.snackbarController.open({
      type: "success",
      message: t("eventActions.statusUpdated"),
    });
    return true;
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : t("eventActions.statusUpdateFailed");
    context.snackbarController.open({ type: "error", message });
    console.error(error);
    return false;
  }
};

export function buildEventManagerEntityAction(
  options: EventEntityActionsOptions
): EntityAction<Event> {
  const {
    accountId,
    isSuperManager,
    isAccountManagerForAccount,
    managedEventKeys,
  } = options;

  return {
    key: "event-manager",
    name: EVENT_MANAGER_LABEL,
    icon: <SettingsIcon />,
    ...tableOnlyEntityAction,
    collapsed: false,
    isEnabled: ({ entity }) => {
      if (!entity) {
        return false;
      }
      if (!isEventManagerAccessible(entity.values.status)) {
        return false;
      }
      if (isSuperManager || isAccountManagerForAccount) {
        return true;
      }
      return managedEventKeys?.has(managedEventKey(accountId, entity.id)) ?? false;
    },
    onClick: ({ entity }) => {
      if (!entity) {
        return;
      }
      openEventFromEntity(entity);
    },
  };
}

export function buildShareEventAdminLinkEntityAction(
  accountId: string
): EntityAction<Event> {
  return {
    key: "share-event-link",
    name: "Share link",
    icon: <LinkIcon />,
    ...tableOnlyEntityAction,
    onClick: async ({ entity, context }) => {
      if (!entity) {
        return;
      }
      const ok = await copyTextToClipboard(
        buildEventAdminUrl({
          accountId,
          eventId: entity.id,
          spaceId: entity.values.space?.id ?? "",
        })
      );
      context.snackbarController.open({
        type: ok ? "success" : "error",
        message: ok ? "Link copied to clipboard" : "Could not copy link",
      });
    },
  };
}

export function buildRepublishEventContentEntityAction(
  accountId: string,
  locale: Locale = getAdminUiLocale()
): EntityAction<Event> {
  const t = getAdminT(locale);
  return {
    key: "republish-content",
    name: t("eventActions.republishName"),
    icon: <SyncAltIcon />,
    ...tableOnlyEntityAction,
    isEnabled: ({ entity }) =>
      EVENT_OPERATIONAL_STATUSES.includes(
        entity?.values.status ?? "provisioning"
      ),
    onClick: ({ entity, context }) => {
      if (!entity) {
        return;
      }

      openConfirmTextAction(context, {
        key: `republish-content-${entity.id}`,
        title: t("eventActions.republishTitle"),
        body: t("eventActions.republishBody"),
        onConfirm: async () => {
          let functions;
          try {
            functions = getFunctions(getApp());
          } catch {
            context.snackbarController.open({
              type: "error",
              message: t("common.unknownError"),
            });
            return false;
          }

          try {
            const callable = httpsCallable(functions, "republishEventContent");
            await callable({ accountId, eventId: entity.id });
            context.snackbarController.open({
              type: "success",
              message: t("eventActions.republishQueued"),
            });
            return true;
          } catch (error: unknown) {
            const message =
              error instanceof Error ? error.message : t("common.unknownError");
            context.snackbarController.open({ type: "error", message });
            console.error(error);
            return false;
          }
        },
      });
    },
  };
}

function buildLifecycleStatusAction(
  options: EventEntityActionsOptions,
  locale: Locale,
  config: {
    key: string;
    name: string;
    icon: ReactElement;
    title: string;
    body: string;
    nextStatus: EventStatus;
    isEnabled: (status: EventStatus | undefined) => boolean;
  }
): EntityAction<Event> {
  return {
    key: config.key,
    name: config.name,
    icon: config.icon,
    ...tableOnlyEntityAction,
    isEnabled: ({ entity }) =>
      isAccountLifecycleManager(options) &&
      config.isEnabled(entity?.values.status),
    onClick: ({ entity, context }) => {
      if (!entity) {
        return;
      }

      openConfirmTextAction(context, {
        key: `${config.key}-${entity.id}`,
        title: config.title,
        body: config.body,
        onConfirm: () =>
          callSetEventStatus(
            context,
            options.accountId,
            entity.id,
            config.nextStatus,
            locale
          ),
      });
    },
  };
}

function buildDeleteEventEntityAction(
  options: EventEntityActionsOptions,
  locale: Locale
): EntityAction<Event> {
  const t = getAdminT(locale);
  return {
    key: "delete-event",
    name: t("eventActions.deleteName"),
    icon: <DeleteIcon />,
    ...tableOnlyEntityAction,
    isEnabled: ({ entity }) =>
      isAccountLifecycleManager(options) &&
      entity?.values.status === "archived",
    onClick: ({ entity, context }) => {
      if (!entity) {
        return;
      }

      openDeleteEventAction(context, {
        key: `delete-event-${entity.id}`,
        accountId: options.accountId,
        eventId: entity.id,
        eventName: entity.values.name,
      });
    },
  };
}

export function buildEventEntityActions(
  options: EventEntityActionsOptions,
  locale: Locale = getAdminUiLocale()
): EntityAction<Event>[] {
  const t = getAdminT(locale);

  const lifecycleActions: EntityAction<Event>[] = options.archivedView
    ? [
        buildLifecycleStatusAction(options, locale, {
          key: "unarchive-event",
          name: t("eventActions.unarchiveName"),
          icon: <UnarchiveIcon />,
          title: t("eventActions.unarchiveTitle"),
          body: t("eventActions.unarchiveBody"),
          nextStatus: "running",
          isEnabled: (status) => status === "archived",
        }),
        buildDeleteEventEntityAction(options, locale),
      ]
    : [
        buildLifecycleStatusAction(options, locale, {
          key: "archive-event",
          name: t("eventActions.archiveName"),
          icon: <ArchiveIcon />,
          title: t("eventActions.archiveTitle"),
          body: t("eventActions.archiveBody"),
          nextStatus: "archived",
          isEnabled: (status) =>
            status != null && EVENT_ARCHIVABLE_STATUSES.includes(status),
        }),
      ];

  return [
    buildEventManagerEntityAction(options),
    buildShareEventAdminLinkEntityAction(options.accountId),
    buildRepublishEventContentEntityAction(options.accountId, locale),
    ...lifecycleActions,
  ];
}
